import { useForm, Controller, FormProvider } from "react-hook-form";
import { Button } from "../../../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { EditBookingMailingSettingsForm } from "./bookingMailingSettings.schema";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/src/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/src/components/ui/select";
import { useEffect } from "react";
import { useToast } from "@/src/components/ui/use-toast";
import { MailingSettings } from "../../Offers/model";
import { useUpdateBookingMailingSettings } from "@/src/lib/bookingMailingSettingsMutation";
import { Switch } from "@/src/components/ui/switch";

type EditBookingMailingSettingsProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  initialSettings: MailingSettings;
  bookingId: string;
};

export const EditBookingMailingSettingsModal = ({
  open,
  setOpen,
  initialSettings,
  bookingId,
}: EditBookingMailingSettingsProps) => {
  const updateBookingMailingSettings = useUpdateBookingMailingSettings(bookingId);
  const { toast } = useToast();
  const form = useForm<EditBookingMailingSettingsForm>({
    defaultValues: {
      mailingSettings: initialSettings,
    },
  });

  const { handleSubmit, control, reset } = form;

  useEffect(() => {
    reset({ mailingSettings: initialSettings });
  }, [initialSettings, reset]);

  const onSubmit = (data: EditBookingMailingSettingsForm) => {
    const toastId = toast({
      description: "Updating booking mailing settings...",
      variant: "default",
      duration: Infinity,
    }).id;
    updateBookingMailingSettings.mutate({ mailingSettings: data.mailingSettings, toastId });
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(val) => {
        if (!val) {
          setOpen(false);
        }
      }}
    >
      <DialogContent className="max-w-[480px] max-h-[calc(100%-60px)] overflow-y-auto">
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogHeader>
              <DialogTitle className="text-3xl font-semi-bold">Mailing settings</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col flex-grow gap-6 w-full mt-8">
              <FormField
                key="mailingSettings.bookingConfirmation"
                control={control}
                name="mailingSettings.bookingConfirmation"
                render={({ field: { onChange, value } }) => {
                  return (
                    <FormItem className={`flex items-center gap-6 justify-between`} required>
                      <FormLabel>Create booking notification:</FormLabel>
                      <FormControl>
                        <Switch onCheckedChange={onChange} checked={value} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
              <FormField
                key="mailingSettings.bookingUpdates"
                control={control}
                name="mailingSettings.bookingUpdates"
                render={({ field: { onChange, value } }) => {
                  return (
                    <FormItem className={`flex items-center gap-6 justify-between`} required>
                      <FormLabel>Booking updates notifications:</FormLabel>
                      <FormControl>
                        <Switch onCheckedChange={onChange} checked={value} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
              <FormField
                key="mailingSettings.paymentConfirmations"
                control={control}
                name="mailingSettings.paymentConfirmations"
                render={({ field: { onChange, value } }) => {
                  return (
                    <FormItem className={`flex items-center gap-6 justify-between`} required>
                      <FormLabel>
                        {" "}
                        Payment confirmation notifications (for manually handled payments in HELMs App):
                      </FormLabel>
                      <FormControl>
                        <Switch onCheckedChange={onChange} checked={value} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
              <FormField
                key="mailingSettings.paymentReminders"
                control={control}
                name="mailingSettings.paymentReminders"
                render={({ field: { onChange, value } }) => {
                  return (
                    <FormItem className={`flex items-center gap-6 justify-between`} required>
                      <FormLabel>Payment reminders notifications:</FormLabel>
                      <FormControl>
                        <Switch onCheckedChange={onChange} checked={value} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            </div>

            <DialogFooter className="flex mt-8 w-full !justify-between">
              <Button type="button" onClick={() => setOpen(false)} variant="outline">
                Cancel
              </Button>
              <Button type="submit" variant="default">
                Submit
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};
